// Medical Scores Database - Complete Collection Part 9
// More specialized scores from medicalcul.free.fr

import {
  Brain,
  Heart,
  Wind,
  Activity,
  Baby,
  Stethoscope,
  Thermometer,
  Droplets,
  Flame,
  Syringe,
  Pill,
  Bone,
  Scale,
  Clock,
  AlertTriangle,
  Calculator,
  Beaker,
  User,
  Target,
  Shield,
  Zap,
  Eye
} from 'lucide-react';

export const SCORES_PART_9 = {
  // ABC transfusion
  abcTransfusion: {
    name: 'Score ABC Transfusion',
    category: 'Urgences',
    icon: Droplets,
    description: 'Prédiction transfusion massive en traumatologie',
    color: 'orange',
    fields: [
      { id: 'penetrating', label: 'Traumatisme pénétrant', type: 'checkbox', points: 1 },
      { id: 'sbp', label: 'PAS ≤ 90 mmHg aux urgences', type: 'checkbox', points: 1 },
      { id: 'hr', label: 'FC ≥ 120/min aux urgences', type: 'checkbox', points: 1 },
      { id: 'fast', label: 'FAST positif', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.penetrating) total += 1;
      if (values.sbp) total += 1;
      if (values.hr) total += 1;
      if (values.fast) total += 1;
      return { score: total, max: 4 };
    },
    interpret: (score) => {
      if (score < 2) return { level: 'success', text: `ABC ${score}`, detail: 'Transfusion massive peu probable' };
      return { level: 'danger', text: `ABC ${score}`, detail: 'Transfusion massive probable - Activation protocole' };
    },
    formula: 'ABC ≥ 2 = transfusion massive probable (Se 75%, Sp 86%)'
  },

  // ABSI - Brûlés
  absi: {
    name: 'Score ABSI',
    category: 'Brûlés',
    icon: Flame,
    description: 'Pronostic brûlé (Abbreviated Burn Severity Index)',
    color: 'orange',
    fields: [
      { id: 'sex', label: 'Sexe', type: 'select', options: [
        { value: 0, label: 'Femme (0 pt)' },
        { value: 1, label: 'Homme (1 pt)' }
      ]},
      { id: 'age', label: 'Âge', type: 'select', options: [
        { value: 1, label: '0-20 ans (1 pt)' },
        { value: 2, label: '21-40 ans (2 pts)' },
        { value: 3, label: '41-60 ans (3 pts)' },
        { value: 4, label: '61-80 ans (4 pts)' },
        { value: 5, label: '> 80 ans (5 pts)' }
      ]},
      { id: 'inhalation', label: 'Lésion d\'inhalation', type: 'checkbox', points: 1 },
      { id: 'fullThickness', label: 'Brûlure pleine épaisseur', type: 'checkbox', points: 1 },
      { id: 'tbsa', label: 'Surface brûlée (% SCT)', type: 'select', options: [
        { value: 1, label: '1-10% (1 pt)' },
        { value: 2, label: '11-20% (2 pts)' },
        { value: 3, label: '21-30% (3 pts)' },
        { value: 4, label: '31-40% (4 pts)' },
        { value: 5, label: '41-50% (5 pts)' },
        { value: 6, label: '51-60% (6 pts)' },
        { value: 7, label: '61-70% (7 pts)' },
        { value: 8, label: '71-80% (8 pts)' },
        { value: 9, label: '81-90% (9 pts)' },
        { value: 10, label: '91-100% (10 pts)' }
      ]}
    ],
    calculate: (values) => {
      let total = (values.sex || 0) + (values.age || 2) + (values.tbsa || 1);
      if (values.inhalation) total += 1;
      if (values.fullThickness) total += 1;
      return { score: total, max: 18 };
    },
    interpret: (score) => {
      const survival = {
        2: 99, 3: 99, 4: 98, 5: 97, 6: 90,
        7: 80, 8: 63, 9: 42, 10: 25, 11: 13,
        12: 6, 13: 3, 14: 1, 15: 0.5
      };
      const surv = survival[Math.min(score, 15)] || '<1';
      if (score <= 5) return { level: 'success', text: `ABSI ${score}`, detail: `Survie ~${surv}%` };
      if (score <= 8) return { level: 'warning', text: `ABSI ${score}`, detail: `Survie ~${surv}%` };
      return { level: 'danger', text: `ABSI ${score}`, detail: `Survie ~${surv}%` };
    },
    formula: 'ABSI = Sexe + Âge + Inhalation + Pleine épaisseur + %SCT'
  },

  // Airtraq
  airtraq: {
    name: 'Score Airtraq',
    category: 'Anesthésie',
    icon: Syringe,
    description: 'Difficulté intubation vidéolaryngoscopie',
    color: 'emerald',
    fields: [
      { id: 'view', label: 'Vue à la vidéolaryngoscopie', type: 'select', options: [
        { value: 1, label: 'Grade 1 - Cordes vocales visibles' },
        { value: 2, label: 'Grade 2 - Partie postérieure des cordes' },
        { value: 3, label: 'Grade 3 - Épiglotte seule' },
        { value: 4, label: 'Grade 4 - Rien' }
      ]}
    ],
    calculate: (values) => {
      return { score: values.view || 1, max: 4 };
    },
    interpret: (score) => {
      if (score <= 2) return { level: 'success', text: `Airtraq Grade ${score}`, detail: 'Intubation facile' };
      if (score === 3) return { level: 'warning', text: `Airtraq Grade ${score}`, detail: 'Intubation difficile - Aide manuelle' };
      return { level: 'danger', text: `Airtraq Grade ${score}`, detail: 'Intubation très difficile - Alternatives' };
    },
    formula: 'Airtraq: classification comme Cormack-Lehane'
  },

  // Aix - Index de masse corporelle ajusté
  aixBodyMassAdjusted: {
    name: 'IMC Ajusté à l\'âge',
    category: 'Nutrition',
    icon: Scale,
    description: 'Interprétation IMC selon l\'âge',
    color: 'green',
    fields: [
      { id: 'weight', label: 'Poids (kg)', type: 'number', min: 30, max: 250 },
      { id: 'height', label: 'Taille (cm)', type: 'number', min: 100, max: 250 },
      { id: 'age', label: 'Âge (ans)', type: 'number', min: 18, max: 120 }
    ],
    calculate: (values) => {
      const w = values.weight || 70;
      const h = (values.height || 170) / 100;
      const bmi = w / (h * h);
      return { score: bmi.toFixed(1), max: '', unit: 'kg/m²', age: values.age || 40 };
    },
    interpret: (score, calculated) => {
      const bmi = parseFloat(score);
      const age = calculated?.age || 40;
      // Seuils ajustés pour personnes âgées
      const lowThreshold = age >= 65 ? 22 : 18.5;
      const highThreshold = age >= 65 ? 27 : 25;
      const obeseThreshold = 30;
      
      if (bmi < lowThreshold) return { level: 'warning', text: `IMC ${score}`, detail: 'Maigreur - Risque de dénutrition' };
      if (bmi < highThreshold) return { level: 'success', text: `IMC ${score}`, detail: 'Poids normal' };
      if (bmi < obeseThreshold) return { level: 'warning', text: `IMC ${score}`, detail: 'Surpoids' };
      if (bmi < 35) return { level: 'danger', text: `IMC ${score}`, detail: 'Obésité grade I' };
      if (bmi < 40) return { level: 'danger', text: `IMC ${score}`, detail: 'Obésité grade II' };
      return { level: 'danger', text: `IMC ${score}`, detail: 'Obésité grade III (morbide)' };
    },
    formula: 'IMC = Poids / Taille². Seuils ajustés si ≥65 ans'
  },

  // Angine de poitrine CCS
  ccAngina: {
    name: 'Classification CCS (Angor)',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Gradation angine de poitrine',
    color: 'rose',
    fields: [
      { id: 'class', label: 'Classe CCS', type: 'select', options: [
        { value: 1, label: 'Classe I - Angor pour efforts importants, prolongés' },
        { value: 2, label: 'Classe II - Limitation légère (marche rapide, escaliers, émotion)' },
        { value: 3, label: 'Classe III - Limitation marquée (marche normale, 1 étage)' },
        { value: 4, label: 'Classe IV - Angor au moindre effort ou repos' }
      ]}
    ],
    calculate: (values) => {
      return { score: values.class || 1, max: 4 };
    },
    interpret: (score) => {
      if (score <= 2) return { level: 'success', text: `CCS Classe ${score}`, detail: 'Angor stable léger - Traitement médical' };
      if (score === 3) return { level: 'warning', text: `CCS Classe ${score}`, detail: 'Angor invalidant - Coronarographie à discuter' };
      return { level: 'danger', text: `CCS Classe ${score}`, detail: 'Angor instable/sévère - Coronarographie' };
    },
    formula: 'CCS I-IV: classification fonctionnelle angor stable'
  },

  // Baux - Brûlés
  baux: {
    name: 'Score de Baux',
    category: 'Brûlés',
    icon: Flame,
    description: 'Pronostic brûlé (classique)',
    color: 'orange',
    fields: [
      { id: 'age', label: 'Âge (ans)', type: 'number', min: 1, max: 100 },
      { id: 'tbsa', label: 'Surface brûlée (%)', type: 'number', min: 1, max: 100 },
      { id: 'inhalation', label: 'Lésion inhalation', type: 'checkbox', points: 17 }
    ],
    calculate: (values) => {
      let total = (values.age || 40) + (values.tbsa || 20);
      if (values.inhalation) total += 17;
      return { score: total, max: 217 };
    },
    interpret: (score) => {
      if (score < 60) return { level: 'success', text: `Baux ${score}`, detail: 'Survie probable > 90%' };
      if (score < 80) return { level: 'warning', text: `Baux ${score}`, detail: 'Survie ~50%' };
      if (score < 100) return { level: 'danger', text: `Baux ${score}`, detail: 'Survie faible' };
      return { level: 'danger', text: `Baux ${score}`, detail: 'Survie très faible - Soins palliatifs à discuter' };
    },
    formula: 'Baux = Âge + %SCT (+17 si inhalation)'
  },

  // Berlin ARDS
  berlinARDS: {
    name: 'Classification de Berlin',
    category: 'Réanimation',
    icon: Activity,
    description: 'Sévérité SDRA',
    color: 'red',
    fields: [
      { id: 'timing', label: 'Début < 7 jours après facteur déclenchant', type: 'checkbox', points: 1 },
      { id: 'imaging', label: 'Opacités bilatérales (radio/TDM)', type: 'checkbox', points: 1 },
      { id: 'edema', label: 'Œdème non cardiogénique (PVC ou écho)', type: 'checkbox', points: 1 },
      { id: 'pao2fio2', label: 'PaO₂/FiO₂ (mmHg) avec PEP ≥5', type: 'select', options: [
        { value: 1, label: '201-300 = Léger' },
        { value: 2, label: '101-200 = Modéré' },
        { value: 3, label: '≤ 100 = Sévère' }
      ]}
    ],
    calculate: (values) => {
      const hasCriteria = values.timing && values.imaging && values.edema;
      const severity = values.pao2fio2 || 0;
      return { score: severity, max: 3, valid: hasCriteria };
    },
    interpret: (score, calculated) => {
      if (!calculated?.valid) return { level: 'warning', text: 'Critères incomplets', detail: 'Vérifier tous les critères obligatoires' };
      if (score === 1) return { level: 'success', text: 'SDRA léger', detail: 'P/F 201-300 - Mortalité ~27%' };
      if (score === 2) return { level: 'warning', text: 'SDRA modéré', detail: 'P/F 101-200 - Mortalité ~32%' };
      return { level: 'danger', text: 'SDRA sévère', detail: 'P/F ≤100 - Mortalité ~45% - DV, ECMO à discuter' };
    },
    formula: 'Berlin: Début + Imagerie + Non cardiogénique + P/F'
  },

  // BSA - Body Surface Area
  bsa: {
    name: 'Surface Corporelle (BSA)',
    category: 'Médecine interne',
    icon: Scale,
    description: 'Surface corporelle (formule Mosteller)',
    color: 'blue',
    fields: [
      { id: 'weight', label: 'Poids (kg)', type: 'number', min: 1, max: 300 },
      { id: 'height', label: 'Taille (cm)', type: 'number', min: 30, max: 250 }
    ],
    calculate: (values) => {
      const w = values.weight || 70;
      const h = values.height || 170;
      const bsa = Math.sqrt((w * h) / 3600);
      return { score: bsa.toFixed(2), max: '', unit: 'm²' };
    },
    interpret: (score) => {
      const bsa = parseFloat(score);
      return { level: 'success', text: `BSA ${score} m²`, detail: 'Utilisé pour chimiothérapie, posologie, etc.' };
    },
    formula: 'BSA (Mosteller) = √[(Poids × Taille) / 3600]'
  },

  // Cappellini - Calcium corrigé
  calciumCorrected: {
    name: 'Calcémie corrigée',
    category: 'Médecine interne',
    icon: Beaker,
    description: 'Correction calcium pour albumine',
    color: 'blue',
    fields: [
      { id: 'calcium', label: 'Calcium total (mmol/L)', type: 'number', min: 1, max: 4, step: 0.01 },
      { id: 'albumin', label: 'Albumine (g/L)', type: 'number', min: 10, max: 60 }
    ],
    calculate: (values) => {
      const ca = values.calcium || 2.3;
      const alb = values.albumin || 40;
      const corrected = ca + 0.02 * (40 - alb);
      return { score: corrected.toFixed(2), max: '', unit: 'mmol/L' };
    },
    interpret: (score) => {
      const ca = parseFloat(score);
      if (ca < 2.20) return { level: 'danger', text: `Ca corrigé ${score}`, detail: 'Hypocalcémie' };
      if (ca <= 2.60) return { level: 'success', text: `Ca corrigé ${score}`, detail: 'Calcémie normale' };
      if (ca <= 3.00) return { level: 'warning', text: `Ca corrigé ${score}`, detail: 'Hypercalcémie légère' };
      if (ca <= 3.50) return { level: 'danger', text: `Ca corrigé ${score}`, detail: 'Hypercalcémie modérée - Traitement' };
      return { level: 'danger', text: `Ca corrigé ${score}`, detail: 'Hypercalcémie sévère - Urgence' };
    },
    formula: 'Ca corrigé = Ca + 0.02 × (40 - Albumine)'
  },

  // Charlson - Comorbidités (version complète)
  charlsonFull: {
    name: 'Score de Charlson (complet)',
    category: 'Médecine interne',
    icon: User,
    description: 'Index de comorbidité avec estimation survie',
    color: 'blue',
    fields: [
      { id: 'age', label: 'Âge', type: 'select', options: [
        { value: 0, label: '< 50 ans (0 pt)' },
        { value: 1, label: '50-59 ans (1 pt)' },
        { value: 2, label: '60-69 ans (2 pts)' },
        { value: 3, label: '70-79 ans (3 pts)' },
        { value: 4, label: '≥ 80 ans (4 pts)' }
      ]},
      { id: 'mi', label: 'Infarctus du myocarde', type: 'checkbox', points: 1 },
      { id: 'chf', label: 'Insuffisance cardiaque congestive', type: 'checkbox', points: 1 },
      { id: 'pvd', label: 'Maladie vasculaire périphérique', type: 'checkbox', points: 1 },
      { id: 'cva', label: 'AVC ou AIT', type: 'checkbox', points: 1 },
      { id: 'dementia', label: 'Démence', type: 'checkbox', points: 1 },
      { id: 'copd', label: 'BPCO', type: 'checkbox', points: 1 },
      { id: 'ctd', label: 'Connectivite', type: 'checkbox', points: 1 },
      { id: 'pud', label: 'Ulcère gastroduodénal', type: 'checkbox', points: 1 },
      { id: 'liverMild', label: 'Hépatopathie légère', type: 'checkbox', points: 1 },
      { id: 'diabetes', label: 'Diabète sans complication', type: 'checkbox', points: 1 },
      { id: 'diabetesComplicated', label: 'Diabète avec atteinte d\'organe', type: 'checkbox', points: 2 },
      { id: 'hemiplegia', label: 'Hémiplégie', type: 'checkbox', points: 2 },
      { id: 'ckd', label: 'Néphropathie modérée à sévère', type: 'checkbox', points: 2 },
      { id: 'solidTumor', label: 'Tumeur solide (sans métastase)', type: 'checkbox', points: 2 },
      { id: 'leukemia', label: 'Leucémie', type: 'checkbox', points: 2 },
      { id: 'lymphoma', label: 'Lymphome', type: 'checkbox', points: 2 },
      { id: 'liverSevere', label: 'Hépatopathie modérée à sévère', type: 'checkbox', points: 3 },
      { id: 'metastatic', label: 'Tumeur solide métastatique', type: 'checkbox', points: 6 },
      { id: 'aids', label: 'SIDA (pas seulement VIH+)', type: 'checkbox', points: 6 }
    ],
    calculate: (values) => {
      let total = values.age || 0;
      if (values.mi) total += 1;
      if (values.chf) total += 1;
      if (values.pvd) total += 1;
      if (values.cva) total += 1;
      if (values.dementia) total += 1;
      if (values.copd) total += 1;
      if (values.ctd) total += 1;
      if (values.pud) total += 1;
      if (values.liverMild && !values.liverSevere) total += 1;
      if (values.diabetes && !values.diabetesComplicated) total += 1;
      if (values.diabetesComplicated) total += 2;
      if (values.hemiplegia) total += 2;
      if (values.ckd) total += 2;
      if (values.solidTumor && !values.metastatic) total += 2;
      if (values.leukemia) total += 2;
      if (values.lymphoma) total += 2;
      if (values.liverSevere) total += 3;
      if (values.metastatic) total += 6;
      if (values.aids) total += 6;
      return { score: total, max: 37 };
    },
    interpret: (score) => {
      // Survie à 10 ans estimée
      if (score === 0) return { level: 'success', text: `Charlson ${score}`, detail: 'Survie 10 ans: 99%' };
      if (score === 1) return { level: 'success', text: `Charlson ${score}`, detail: 'Survie 10 ans: 96%' };
      if (score === 2) return { level: 'success', text: `Charlson ${score}`, detail: 'Survie 10 ans: 90%' };
      if (score === 3) return { level: 'warning', text: `Charlson ${score}`, detail: 'Survie 10 ans: 77%' };
      if (score === 4) return { level: 'warning', text: `Charlson ${score}`, detail: 'Survie 10 ans: 53%' };
      if (score <= 6) return { level: 'danger', text: `Charlson ${score}`, detail: 'Survie 10 ans: 21%' };
      return { level: 'danger', text: `Charlson ${score}`, detail: 'Survie 10 ans: < 2%' };
    },
    formula: 'Charlson: 19 items pondérés (1-6 pts) + âge'
  },

  // CRB65 - Pneumonie
  crb65: {
    name: 'Score CRB-65',
    category: 'Infectiologie',
    icon: Thermometer,
    description: 'Sévérité pneumonie communautaire (sans biologie)',
    color: 'red',
    fields: [
      { id: 'confusion', label: 'C - Confusion', type: 'checkbox', points: 1 },
      { id: 'respiratory', label: 'R - FR ≥ 30/min', type: 'checkbox', points: 1 },
      { id: 'bp', label: 'B - PAS < 90 ou PAD ≤ 60 mmHg', type: 'checkbox', points: 1 },
      { id: 'age', label: '65 - Âge ≥ 65 ans', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.confusion) total += 1;
      if (values.respiratory) total += 1;
      if (values.bp) total += 1;
      if (values.age) total += 1;
      return { score: total, max: 4 };
    },
    interpret: (score) => {
      if (score === 0) return { level: 'success', text: `CRB-65 ${score}`, detail: 'Traitement ambulatoire possible' };
      if (score <= 2) return { level: 'warning', text: `CRB-65 ${score}`, detail: 'Hospitalisation à considérer' };
      return { level: 'danger', text: `CRB-65 ${score}`, detail: 'Hospitalisation urgente - USI à discuter' };
    },
    formula: 'CRB-65 = Confusion + Resp≥30 + BP + Âge≥65'
  },

  // CRUSADE - Saignement SCA
  crusade: {
    name: 'Score CRUSADE',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Risque hémorragique majeur SCA',
    color: 'rose',
    fields: [
      { id: 'hematocrit', label: 'Hématocrite (%)', type: 'select', options: [
        { value: 0, label: '≥ 40% (0 pt)' },
        { value: 2, label: '37-39.9% (2 pts)' },
        { value: 3, label: '34-36.9% (3 pts)' },
        { value: 7, label: '31-33.9% (7 pts)' },
        { value: 9, label: '< 31% (9 pts)' }
      ]},
      { id: 'creatinine', label: 'Clairance créatinine (mL/min)', type: 'select', options: [
        { value: 0, label: '> 120 (0 pt)' },
        { value: 7, label: '91-120 (7 pts)' },
        { value: 17, label: '61-90 (17 pts)' },
        { value: 28, label: '31-60 (28 pts)' },
        { value: 35, label: '16-30 (35 pts)' },
        { value: 39, label: '≤ 15 (39 pts)' }
      ]},
      { id: 'hr', label: 'Fréquence cardiaque (bpm)', type: 'select', options: [
        { value: 0, label: '≤ 70 (0 pt)' },
        { value: 1, label: '71-80 (1 pt)' },
        { value: 3, label: '81-90 (3 pts)' },
        { value: 6, label: '91-100 (6 pts)' },
        { value: 8, label: '101-110 (8 pts)' },
        { value: 10, label: '111-120 (10 pts)' },
        { value: 11, label: '> 120 (11 pts)' }
      ]},
      { id: 'sbp', label: 'PAS (mmHg)', type: 'select', options: [
        { value: 10, label: '≤ 90 (10 pts)' },
        { value: 8, label: '91-100 (8 pts)' },
        { value: 5, label: '101-120 (5 pts)' },
        { value: 1, label: '121-180 (1 pt)' },
        { value: 3, label: '181-200 (3 pts)' },
        { value: 5, label: '> 200 (5 pts)' }
      ]},
      { id: 'sex', label: 'Sexe féminin', type: 'checkbox', points: 8 },
      { id: 'chf', label: 'Insuffisance cardiaque à l\'admission', type: 'checkbox', points: 7 },
      { id: 'vascular', label: 'Maladie vasculaire connue', type: 'checkbox', points: 6 },
      { id: 'diabetes', label: 'Diabète', type: 'checkbox', points: 6 }
    ],
    calculate: (values) => {
      let total = (values.hematocrit || 0) + (values.creatinine || 0) + 
                  (values.hr || 0) + (values.sbp || 0);
      if (values.sex) total += 8;
      if (values.chf) total += 7;
      if (values.vascular) total += 6;
      if (values.diabetes) total += 6;
      return { score: total, max: 96 };
    },
    interpret: (score) => {
      if (score <= 20) return { level: 'success', text: `CRUSADE ${score}`, detail: 'Risque hémorragique très faible (~3%)' };
      if (score <= 30) return { level: 'success', text: `CRUSADE ${score}`, detail: 'Risque faible (~6%)' };
      if (score <= 40) return { level: 'warning', text: `CRUSADE ${score}`, detail: 'Risque modéré (~9%)' };
      if (score <= 50) return { level: 'warning', text: `CRUSADE ${score}`, detail: 'Risque élevé (~12%)' };
      return { level: 'danger', text: `CRUSADE ${score}`, detail: 'Risque très élevé (~19%)' };
    },
    formula: 'CRUSADE: 8 variables = risque saignement majeur intra-hospitalier'
  },

  // DRS - Diabetic Retinopathy Screening
  drs: {
    name: 'Classification rétinopathie diabetique',
    category: 'Ophtalmologie',
    icon: Eye,
    description: 'Stade rétinopathie diabetique',
    color: 'amber',
    fields: [
      { id: 'stage', label: 'Stade', type: 'select', options: [
        { value: 0, label: 'Pas de rétinopathie' },
        { value: 1, label: 'RD non proliférante minime (micro-anévrismes)' },
        { value: 2, label: 'RDNP modérée' },
        { value: 3, label: 'RDNP sévère (règle 4-2-1)' },
        { value: 4, label: 'RD proliférante' }
      ]},
      { id: 'macular', label: 'Œdème maculaire', type: 'select', options: [
        { value: 0, label: 'Absent' },
        { value: 1, label: 'Présent' }
      ]}
    ],
    calculate: (values) => {
      return { score: values.stage || 0, max: 4, macular: values.macular || 0 };
    },
    interpret: (score, calculated) => {
      const macular = calculated?.macular ? ' + Œdème maculaire' : '';
      if (score === 0) return { level: 'success', text: `Pas de RD${macular}`, detail: 'Dépistage annuel' };
      if (score === 1) return { level: 'success', text: `RDNP minime${macular}`, detail: 'Dépistage annuel' };
      if (score === 2) return { level: 'warning', text: `RDNP modérée${macular}`, detail: 'Surveillance 6-12 mois' };
      if (score === 3) return { level: 'danger', text: `RDNP sévère${macular}`, detail: 'Avis ophtalmo rapide - PPR à discuter' };
      return { level: 'danger', text: `RD proliférante${macular}`, detail: 'Avis ophtalmo urgent - PPR indiquée' };
    },
    formula: 'RD: 5 stades. Règle 4-2-1 définit RDNP sévère'
  }
};
